import React, { useState } from 'react';
import {
  CheckIcon,
  Loader2Icon,
  XIcon,
  PlusIcon,
  PencilIcon,
  Trash2Icon,
  AlertTriangleIcon,
  EyeIcon,
  CodeIcon,
  Wand2Icon,
  SendIcon,
  Undo2Icon,
  ListOrderedIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from 'lucide-react';
import { QuestionOp } from '../../lib/api-types';
import MarkdownRenderer from '../MarkdownRenderer';
import DiffText from './DiffText';

export type OpStatus = 'pending' | 'applying' | 'applied' | 'failed';

interface OpDiffCardProps {
  op: QuestionOp;
  decision?: 'approved' | 'rejected';
  onApprove: () => void;
  onReject: () => void;
  onUndo: () => void;
  onRefine?: (instruction: string) => void;
  status?: OpStatus;
  index?: number;
}

const FLAG_LABELS: Record<string, string> = {
  is_first_class: 'First class',
  is_lars: 'LARS',
  is_active: 'Active',
  is_faq: 'FAQ',
  is_conversion: 'Conversion',
  is_optimizable: 'Optimizable',
};

const ACTION_META: Record<QuestionOp['action'], { label: string; cls: string; Icon: React.FC<any> }> = {
  create: { label: 'Create', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: PlusIcon },
  update: { label: 'Update', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200', Icon: PencilIcon },
  delete: { label: 'Delete', cls: 'bg-rose-50 text-rose-700 border-rose-200', Icon: Trash2Icon },
  reorder: { label: 'Reorder', cls: 'bg-violet-50 text-violet-700 border-violet-200', Icon: ListOrderedIcon },
};

const BoolChip: React.FC<{ value: boolean }> = ({ value }) => (
  <span
    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${
      value ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
    }`}
  >
    {value ? 'true' : 'false'}
  </span>
);

const FieldRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-center gap-2 text-[13px]">
    <span className="text-gray-500 w-28 flex-shrink-0">{label}</span>
    <div className="flex items-center gap-2 flex-wrap">{children}</div>
  </div>
);

const StatusBadge: React.FC<{ status?: OpStatus }> = ({ status }) => {
  if (status === 'applying')
    return <Loader2Icon className="h-4 w-4 text-indigo-500 animate-spin" />;
  if (status === 'applied') return <CheckIcon className="h-4 w-4 text-emerald-500" />;
  if (status === 'failed') return <XIcon className="h-4 w-4 text-rose-500" />;
  return null;
};

const OpDiffCard: React.FC<OpDiffCardProps> = ({
  op,
  decision,
  onApprove,
  onReject,
  onUndo,
  onRefine,
  status,
  index = 0,
}) => {
  const [previewAnswer, setPreviewAnswer] = useState(false);
  const [showRefine, setShowRefine] = useState(false);
  const [refineText, setRefineText] = useState('');
  const { label, cls, Icon } = ACTION_META[op.action];
  const before = op.before || {};
  const after = op.after || {};
  const changed = op.changed_fields || [];
  const locked = status === 'applying' || status === 'applied';
  const approved = decision === 'approved';
  const rejected = decision === 'rejected';

  const submitRefine = () => {
    const t = refineText.trim();
    if (!t || !onRefine) return;
    onRefine(t);
    setRefineText('');
    setShowRefine(false);
  };

  return (
    <div
      className={`border rounded bg-white animate-fade-in-up transition-colors ${
        rejected ? 'border-rose-200 opacity-60' : approved ? 'border-emerald-300' : 'border-gray-200'
      }`}
      style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
    >
      {/* Header */}
      <div className="flex items-start gap-3 px-3.5 py-3 border-b border-gray-100">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold border ${cls} flex-shrink-0`}>
          <Icon className="h-3 w-3" />
          {label}
        </span>
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-medium line-clamp-2 ${rejected ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{op.label}</p>
          {op.reason && <p className="mt-0.5 text-xs text-gray-500 line-clamp-2">{op.reason}</p>}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <StatusBadge status={status} />
          {!locked && (
            <>
              <button
                type="button"
                onClick={onApprove}
                aria-pressed={approved}
                title="Approve this change"
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                  approved
                    ? 'bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-emerald-400 hover:text-emerald-600'
                }`}
              >
                <CheckIcon className="h-3.5 w-3.5" />
                {approved ? 'Approved' : 'Approve'}
              </button>
              <button
                type="button"
                onClick={onReject}
                aria-pressed={rejected}
                title="Reject this change"
                className={`inline-flex items-center justify-center w-7 h-7 rounded border transition-colors ${
                  rejected
                    ? 'bg-rose-500 text-white border-rose-500 hover:bg-rose-600'
                    : 'bg-white text-gray-400 border-gray-300 hover:border-rose-400 hover:text-rose-600'
                }`}
              >
                <XIcon className="h-4 w-4" />
              </button>
              {decision && (
                <button
                  type="button"
                  onClick={onUndo}
                  title="Undo — back to pending"
                  className="inline-flex items-center gap-1 px-1.5 py-1 rounded text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <Undo2Icon className="h-3.5 w-3.5" />
                  Undo
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-3.5 py-3 space-y-3">
        {op.baselined && (
          <div className="flex items-start gap-1.5 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
            <AlertTriangleIcon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>
              {op.action === 'delete'
                ? 'Baselined question — it will be retired (kept for scoring) rather than hard-deleted.'
                : 'Baselined question — editing its text creates a new version server-side.'}
            </span>
          </div>
        )}

        {/* REORDER */}
        {op.action === 'reorder' && (
          <ol className="space-y-1">
            {(after.reorder || []).map((it) => {
              const moved = it.from != null && it.from !== it.to;
              const up = moved && (it.from as number) > it.to;
              return (
                <li
                  key={it.id}
                  className={`flex items-center gap-2 text-[13px] ${moved ? 'font-medium text-gray-900' : 'text-gray-500'}`}
                >
                  <span className="w-6 text-right tabular-nums text-gray-400">{it.to}.</span>
                  <span className="flex-1 line-clamp-1">{it.label}</span>
                  {moved && (
                    <span className={`inline-flex items-center gap-0.5 text-[11px] flex-shrink-0 ${up ? 'text-emerald-600' : 'text-indigo-600'}`}>
                      {up ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />}
                      was&nbsp;{it.from}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        )}

        {/* CREATE */}
        {op.action === 'create' && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-900">{after.question}</p>
            {after.answer && <MarkdownRenderer content={after.answer} className="text-[13px] text-gray-700" />}
            <div className="flex flex-wrap gap-1.5 pt-1">
              {(['is_faq', 'is_conversion', 'is_first_class'] as const).map((f) =>
                after[f] ? (
                  <span key={f} className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-emerald-100 text-emerald-700">
                    {FLAG_LABELS[f]}
                  </span>
                ) : null,
              )}
              {(after.add_labels || []).map((l) => (
                <span key={l} className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] bg-indigo-100 text-indigo-700">
                  {l}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* DELETE */}
        {op.action === 'delete' && (
          <div className="rounded border border-rose-200 bg-rose-50/50 px-3 py-2">
            <p className="text-sm text-gray-800">{before.question}</p>
            {before.answer && (
              <p className="mt-1 text-[13px] text-gray-500 whitespace-pre-wrap break-words line-clamp-4">{before.answer}</p>
            )}
          </div>
        )}

        {/* UPDATE */}
        {op.action === 'update' && (
          <div className="space-y-3">
            {changed.includes('question') && (
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 mb-1">Question</p>
                <DiffText oldText={before.question} newText={after.question} />
              </div>
            )}

            {changed.includes('answer') && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Answer</p>
                  <button
                    type="button"
                    onClick={() => setPreviewAnswer((v) => !v)}
                    className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-indigo-600"
                  >
                    {previewAnswer ? <CodeIcon className="h-3 w-3" /> : <EyeIcon className="h-3 w-3" />}
                    {previewAnswer ? 'Show diff' : 'Preview new'}
                  </button>
                </div>
                {previewAnswer ? (
                  <div className="rounded border border-emerald-200 bg-white px-3 py-2 max-h-72 overflow-y-auto">
                    <MarkdownRenderer content={after.answer || ''} className="text-[13px] text-gray-800" />
                  </div>
                ) : (
                  <DiffText oldText={before.answer} newText={after.answer} />
                )}
              </div>
            )}

            {Object.keys(FLAG_LABELS)
              .filter((f) => changed.includes(f))
              .map((f) => (
                <FieldRow key={f} label={FLAG_LABELS[f]}>
                  <BoolChip value={!!(before as any)[f]} />
                  <span className="text-gray-400">→</span>
                  <BoolChip value={!!(after as any)[f]} />
                </FieldRow>
              ))}

            {changed.includes('context_type') && (
              <FieldRow label="Context type">
                <span className="px-1.5 py-0.5 rounded text-[11px] bg-gray-100 text-gray-500">{before.context_type || '—'}</span>
                <span className="text-gray-400">→</span>
                <span className="px-1.5 py-0.5 rounded text-[11px] bg-indigo-100 text-indigo-700">{after.context_type || '—'}</span>
              </FieldRow>
            )}

            {changed.includes('labels') && (
              <FieldRow label="Labels">
                {(after.add_labels || []).map((l) => (
                  <span key={`a-${l}`} className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] bg-emerald-100 text-emerald-700">
                    + {l}
                  </span>
                ))}
                {(after.remove_labels || []).map((l) => (
                  <span key={`r-${l}`} className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] bg-rose-100 text-rose-700 line-through">
                    {l}
                  </span>
                ))}
              </FieldRow>
            )}

            {changed.includes('follow_ups') && (
              <div className="space-y-1.5">
                {[
                  { key: 'add_children', label: 'Add follow-up', add: true },
                  { key: 'remove_children', label: 'Remove follow-up', add: false },
                  { key: 'add_parents', label: 'Follow-up of', add: true },
                  { key: 'remove_parents', label: 'Detach from', add: false },
                ].map(({ key, label: rl, add }) => {
                  const items = (after as any)[key] as { id: string; label: string }[] | undefined;
                  if (!items || !items.length) return null;
                  return (
                    <FieldRow key={key} label={rl}>
                      {items.map((it) => (
                        <span
                          key={it.id}
                          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] max-w-[220px] truncate ${
                            add ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700 line-through'
                          }`}
                          title={it.label}
                        >
                          {it.label}
                        </span>
                      ))}
                    </FieldRow>
                  );
                })}
              </div>
            )}

            {changed.includes('ctas') && (
              <FieldRow label="CTAs">
                {(after.add_ctas || []).map((c) => (
                  <span key={`ac-${c.id}`} className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] bg-emerald-100 text-emerald-700">
                    + {c.label}
                  </span>
                ))}
                {(after.remove_ctas || []).map((c) => (
                  <span key={`rc-${c.id}`} className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] bg-rose-100 text-rose-700 line-through">
                    {c.label}
                  </span>
                ))}
              </FieldRow>
            )}
          </div>
        )}
      </div>

      {/* Refine footer */}
      {onRefine && !locked && !rejected && (
        <div className="px-3.5 pb-3 -mt-1">
          {!showRefine ? (
            <button
              type="button"
              onClick={() => setShowRefine(true)}
              className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-indigo-600 transition-colors"
            >
              <Wand2Icon className="h-3.5 w-3.5" />
              Refine this change
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                value={refineText}
                onChange={(e) => setRefineText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submitRefine();
                  } else if (e.key === 'Escape') {
                    setShowRefine(false);
                    setRefineText('');
                  }
                }}
                placeholder="How should I change this? e.g. make it shorter"
                className="flex-1 text-[13px] text-gray-800 bg-white border border-gray-200 rounded px-2.5 py-1.5 outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 placeholder-gray-400"
              />
              <button
                type="button"
                onClick={submitRefine}
                disabled={!refineText.trim()}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium text-white bg-indigo-500 hover:bg-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <SendIcon className="h-3.5 w-3.5" />
                Send
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowRefine(false);
                  setRefineText('');
                }}
                className="px-2 py-1.5 text-xs text-gray-400 hover:text-gray-600"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OpDiffCard;
